import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiX, FiAlertTriangle, FiArrowLeft } from 'react-icons/fi';
import DataEnrichmentCommercialCompare from './DataEnrichmentCommercialCompare';
import {
  CommercialFieldValues, EMPTY_FIELDS, mapFenrisResponseToFields, toFieldString,
  fetchClients, fetchClientById, fetchExistingClRatingDetails, fetchFenrisDetails,
} from './dataEnrichmentCommercialApi';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';
import '../../styles/QuoteProposal.css';
import './DataEnrichmentCommercial.css';
import { useToast } from '../../context/ToastContext';

interface Client {
  databaseId: string;
  name: string;
  commercialName?: string;
  addressLine1?: string;
  city?: string;
  insuredType?: number;
}

interface MatchCandidate { name: string; [key: string]: any; }

const DataEnrichmentCommercialSearch: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [clientSearch, setClientSearch] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientDbId, setClientDbId] = useState('');
  const [clientList, setClientList] = useState<Client[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState<CommercialFieldValues>({ ...EMPTY_FIELDS });
  const [description, setDescription] = useState('');
  const [fenris, setFenris] = useState<CommercialFieldValues>({ ...EMPTY_FIELDS });
  const [matches, setMatches] = useState<MatchCandidate[]>([]);
  const [status, setStatus] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [addressMissing, setAddressMissing] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [noCreditsMessage, setNoCreditsMessage] = useState('');

  const doSearch = async (query: string) => {
    try {
      const res = await fetchClients(query);
      setClientList(res.data || []);
    } catch { setClientList([]); }
    finally { setClientLoading(false); }
  };

  useEffect(() => {
    if (!clientSearch.trim() || clientDbId) { setClientList([]); setClientLoading(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setClientLoading(true);
    debounceRef.current = setTimeout(() => doSearch(clientSearch), 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [clientSearch, clientDbId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadClientAndEnrich = useCallback(async (insuredDatabaseId: string) => {
    setLoading(true);
    setAddressMissing(false);
    setRateLimited(false);
    setNoCreditsMessage('');
    setStatus('');
    try {
      const profileRes = await fetchClientById(insuredDatabaseId);
      const info = profileRes.data?.[0];
      if (!info) { showError('Error while getting insured data.'); setLoading(false); return; }

      const existingFields: CommercialFieldValues = {
        ...EMPTY_FIELDS,
        name: toFieldString(info.commercialName),
        addressLine1: toFieldString(info.addressLine1),
        city: toFieldString(info.city),
        state: toFieldString(info.stateNameOrAbbreviation),
        zipCode: toFieldString(info.zipCode),
        phoneNumber: toFieldString(info.phone),
      };

      try {
        const clRes = await fetchExistingClRatingDetails(insuredDatabaseId);
        if (clRes.status !== 204) {
          const clInfo = clRes.data || {};
          existingFields.primaryNaicsCode = toFieldString(clInfo.primaryNaicCode);
          existingFields.primarySICCode = toFieldString(clInfo.primarySicCode);
          existingFields.yearEstablished = toFieldString(clInfo.yearEstablished);
          existingFields.estimatedPayroll = toFieldString(clInfo.estimatedPayroll);
          existingFields.employees = toFieldString(clInfo.employee);
          existingFields.revenue = toFieldString(clInfo.revenue);
          setDescription(toFieldString(clInfo.description));
        }
      } catch { /* no existing CL rating data — proceed with blanks */ }

      setExisting(existingFields);

      if (!existingFields.addressLine1 || !existingFields.state || !existingFields.city || !existingFields.zipCode) {
        setAddressMissing(true);
        setLoading(false);
        return;
      }

      const fenrisRes = await fetchFenrisDetails(
        existingFields.name, existingFields.addressLine1, existingFields.state,
        existingFields.city, existingFields.zipCode, insuredDatabaseId,
      );
      const data = fenrisRes.data;
      if (data.status === 'Multiple Matches Found') {
        const candidates: MatchCandidate[] = data.fenrisResponse.matchCandidates || [];
        setMatches(candidates);
        setFenris(mapFenrisResponseToFields(candidates[0]));
        setTransactionId(data.fenrisResponse.transcationId || '');
        setStatus('Multiple Matches Found');
      } else if (data.status === 'Success') {
        setMatches([]);
        setFenris(mapFenrisResponseToFields(data.fenrisResponse));
        setTransactionId(data.fenrisResponse.transcationId || '');
        setStatus('Success');
      } else if (data.status === 'Failed') {
        setNoCreditsMessage(data.errorMessage || 'Unable to retrieve Fenris data.');
        showSuccess(data.errorMessage || '');
      }
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setRateLimited(true);
      } else if (err?.response?.status === 400) {
        setNoCreditsMessage(err.response.data?.errorMessage || 'Please add address information in NowCerts AMS.');
      } else {
        showError('Error while getting fenris data.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const onClientSelect = (client: Client) => {
    const displayName = client.insuredType === 0 ? client.commercialName || client.name : client.name;
    setClientName(displayName);
    setClientDbId(client.databaseId);
    setClientSearch(displayName);
    setClientList([]);
    setClientError('');
    loadClientAndEnrich(client.databaseId);
  };

  const onSelectMatch = (index: number) => {
    if (matches[index]) setFenris(mapFenrisResponseToFields(matches[index]));
  };

  const onReset = () => {
    setClientSearch(''); setClientName(''); setClientDbId(''); setClientList([]); setClientError('');
    setExisting({ ...EMPTY_FIELDS }); setFenris({ ...EMPTY_FIELDS }); setDescription('');
    setMatches([]); setStatus(''); setTransactionId('');
    setAddressMissing(false); setRateLimited(false); setNoCreditsMessage('');
  };

  const clearClient = () => {
    setClientSearch(''); setClientName(''); setClientDbId(''); setClientList([]); setClientError('');
  };

  const showCompare = !loading && !addressMissing && !rateLimited && !noCreditsMessage
    && (status === 'Success' || status === 'Multiple Matches Found');

  return (
    <>
      <motion.div className="ho-search" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}>
        <div className="ho-card ho-card--form">
          <div className="ho-step-label">
            <span className="ho-step-num">1</span> Existing Client
          </div>
          <div className="cs-client-search">
            <div className="cs-client-search__input-wrap" style={{ borderColor: clientError ? 'var(--accent-red, #ef4444)' : undefined }}>
              <FiSearch size={15} className="cs-client-search__icon" />
              <input
                className="cs-client-search__input"
                type="text"
                placeholder="Search by client name…"
                value={clientSearch}
                onChange={(e) => {
                  setClientSearch(e.target.value);
                  if (clientError) setClientError('');
                  if (clientName) { setClientName(''); setClientDbId(''); }
                }}
              />
              {clientLoading && <span className="cs-client-search__spinner" />}
              {clientName && !clientLoading && (
                <button className="cs-client-search__clear" onClick={clearClient}>
                  <FiX size={14} />
                </button>
              )}
            </div>
            {clientList.length > 0 && (
              <ul className="cs-client-dropdown">
                {clientList.map((c) => {
                  const name = c.insuredType === 0 ? c.commercialName || c.name : c.name;
                  return (
                    <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => onClientSelect(c)}>
                      <span className="cs-client-dropdown__name">{name}</span>
                      {c.addressLine1 && <span className="cs-client-dropdown__email">{c.addressLine1}, {c.city}</span>}
                    </li>
                  );
                })}
              </ul>
            )}
            {clientSearch.trim() && !clientLoading && clientList.length === 0 && !clientName && (
              <ul className="cs-client-dropdown">
                <li className="cs-client-dropdown__item cs-client-dropdown__item--empty">No clients found</li>
              </ul>
            )}
          </div>

          {loading && (
            <div className="qp-history-loader dec-loader--compact">
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiSearch size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Fetching Data</p>
                <p className="qp-history-loader__sub">Loading AMS and Fenris details<span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span></p>
              </div>
            </div>
          )}

          {!loading && addressMissing && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="ho-step-divider" />
              <div className="dec-alert-box dec-alert-box--info">
                <FiAlertTriangle size={18} style={{ color: '#60a5fa', flexShrink: 0 }} />
                <div className="dec-alert-box__text">
                  Please add address information in NowCerts AMS.
                </div>
              </div>
            </motion.div>
          )}

          {!loading && rateLimited && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="ho-step-divider" />
              <div className="dec-alert-box dec-alert-box--warning">
                <FiAlertTriangle size={18} style={{ color: '#fbbf24', flexShrink: 0 }} />
                <div className="dec-alert-box__text">
                  Please wait 20 seconds before submitting another Prefill request.
                </div>
              </div>
            </motion.div>
          )}

          {!loading && noCreditsMessage && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="ho-step-divider" />
              <div className="dec-alert-box dec-alert-box--warning">
                <FiAlertTriangle size={18} style={{ color: '#fbbf24', flexShrink: 0 }} />
                <div className="dec-alert-box__text">{noCreditsMessage}</div>
              </div>
            </motion.div>
          )}

          {(addressMissing || rateLimited || noCreditsMessage) && !loading && (
            <div className="dec-flex-end">
              <button className="ho-link-btn" onClick={onReset}>New Search</button>
            </div>
          )}

          {showCompare && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
              <div className="ho-step-divider" />
              <div className="ho-result-topbar">
                <button className="ho-back-btn" onClick={onReset}><FiArrowLeft size={14} /> Back</button>
              </div>
              <DataEnrichmentCommercialCompare
                insuredId={clientDbId}
                description={description}
                existing={existing}
                fenris={fenris}
                matches={matches}
                onSelectMatch={onSelectMatch}
                transactionId={transactionId}
              />
            </motion.div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default DataEnrichmentCommercialSearch;
